import { getClass } from 'app/frontend/base/services/characterValues'
import { RaidPlanActor } from 'app/frontend/types/custom/RaidPlan'
import CharacterRole from 'app/frontend/types/custom/CharacterRole'
import { SpecializationOption } from '../hooks/useTeamPlanComputedData'
import { v4 as uuid } from 'uuid'

export type TeamPlanRosterImportCandidate = {
  characterId: number
  classId: number | null
  specId: number | null
  role: CharacterRole | null
  name?: string | null
}

type TeamPlanRosterImportResult = {
  actors: RaidPlanActor[]
  addedCount: number
  mergedCount: number
  skippedCount: number
}

const toClassSpecKey = (classId: number, specId: number | null) =>
  `${classId}:${specId ?? 'none'}`

const cloneActor = (actor: RaidPlanActor): RaidPlanActor => ({
  ...actor,
  casts: actor.casts.map((cast) => ({ ...cast })),
})

const getNextActorLabel = (
  actor: Pick<RaidPlanActor, 'class_id' | 'spec_id'>,
  classSpecCount: Record<string, number>,
  specializationById: Record<number, SpecializationOption>,
) => {
  const key = toClassSpecKey(actor.class_id, actor.spec_id)
  const nextCount = (classSpecCount[key] ?? 0) + 1
  classSpecCount[key] = nextCount
  if (actor.spec_id) {
    const specialization = specializationById[actor.spec_id]
    if (specialization) {
      return `${specialization.name} ${specialization.className} ${nextCount}`
    }
  }
  const className = getClass(actor.class_id.toString()) || 'Unknown'
  return `${className} ${nextCount}`
}

const applyTeamPlanRosterImport = ({
  actors,
  candidates,
  specializations,
}: {
  actors: RaidPlanActor[]
  candidates: TeamPlanRosterImportCandidate[]
  specializations: SpecializationOption[]
}): TeamPlanRosterImportResult => {
  const specializationById = specializations.reduce<
    Record<number, SpecializationOption>
  >((acc, specialization) => {
    acc[specialization.id] = specialization
    return acc
  }, {})
  const nextActors = actors.map(cloneActor)
  const preferredCharacterIds = new Set<number>(
    nextActors
      .map((actor) => actor.preferred_character_id)
      .filter(
        (characterId): characterId is number => typeof characterId === 'number',
      ),
  )
  const classSpecCount = nextActors.reduce<Record<string, number>>(
    (acc, actor) => {
      const key = toClassSpecKey(actor.class_id, actor.spec_id)
      acc[key] = (acc[key] ?? 0) + 1
      return acc
    },
    {},
  )
  let addedCount = 0
  let mergedCount = 0
  let skippedCount = 0

  candidates.forEach((candidate) => {
    if (preferredCharacterIds.has(candidate.characterId)) {
      skippedCount += 1
      return
    }

    const spec = candidate.specId
      ? specializationById[candidate.specId]
      : undefined
    const resolvedClassId = spec?.classId ?? candidate.classId
    const isHealerWithExplicitSpec =
      candidate.specId !== null &&
      (spec?.defaultRole === 'heal' || candidate.role === 'heal')
    const specMatchIndex =
      candidate.specId === null
        ? -1
        : nextActors.findIndex(
            (actor) =>
              actor.spec_id === candidate.specId &&
              (actor.preferred_character_id === null ||
                actor.preferred_character_id === undefined),
          )

    if (specMatchIndex >= 0) {
      nextActors[specMatchIndex].preferred_character_id = candidate.characterId
      preferredCharacterIds.add(candidate.characterId)
      mergedCount += 1
      return
    }

    const classOnlyMatchIndex =
      resolvedClassId === null || isHealerWithExplicitSpec
        ? -1
        : nextActors.findIndex(
            (actor) =>
              actor.class_id === resolvedClassId &&
              actor.spec_id === null &&
              (actor.preferred_character_id === null ||
                actor.preferred_character_id === undefined),
          )

    if (classOnlyMatchIndex >= 0) {
      nextActors[classOnlyMatchIndex].preferred_character_id =
        candidate.characterId
      preferredCharacterIds.add(candidate.characterId)
      mergedCount += 1
      return
    }

    if (resolvedClassId === null) {
      skippedCount += 1
      return
    }

    const newActor: RaidPlanActor = {
      uuid: uuid(),
      spec_id: candidate.specId,
      class_id: resolvedClassId,
      preferred_character_id: candidate.characterId,
      label: '',
      casts: [],
    }
    newActor.label = getNextActorLabel(
      newActor,
      classSpecCount,
      specializationById,
    )
    nextActors.push(newActor)
    preferredCharacterIds.add(candidate.characterId)
    addedCount += 1
  })

  return {
    actors: nextActors,
    addedCount,
    mergedCount,
    skippedCount,
  }
}

export default applyTeamPlanRosterImport
